import React, {useCallback, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import {metrics} from '~/vendor/metrics';

import ContentCard from '~/components/ContentCard';
import SideToDrop from './SideToDrop';
import {Button} from '@customink/pigment-react';
// redux
import {useSelector, useDispatch} from 'react-redux';
import {
  getAnchorStyle,
  getSelectedProduct,
  getSelectedProductColor,
  getViewConstraint,
  getProductViews,
  getDroppedViewInfo as getDroppedViewInfoSelector
} from '~/selectors/ProductSelectors';
import {getIconsArray} from '~/selectors/CanvasSelectors';
import {
  getItemizedPrices,
  getItemizedPricesWithoutDroppedView
} from '~/selectors/QuoteSelectors';

import {updateDroppedView} from '~/actions/QuoteActions';
import {
  removeIconsSelectedProductView,
  selectRemainingProductView
} from '~/actions/ProductActions';

import {DEFAULT_VIEWS, VIEW_ID_MAP} from '~/constants/ProductConstants';

import './index.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export const DropSideCard = ({onClose = () => {}, savings}) => {
  const dispatch = useDispatch();

  const anchorStyle = useSelector(getAnchorStyle);
  const icons = useSelector(getIconsArray);
  const itemizedPrices = useSelector(getItemizedPrices);
  const itemizedPricesWithoutDroppedView = useSelector(
    getItemizedPricesWithoutDroppedView
  );
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedProductColor = useSelector(getSelectedProductColor);
  const productViews = useSelector(getProductViews)(anchorStyle);
  const viewConstraint = useSelector(getViewConstraint);
  const getDroppedViewInfo = useSelector(getDroppedViewInfoSelector);

  const [selectedProductViewId, setSelectedProductViewId] = useState(null);
  const [showMustSelectSideWarning, setShowMustSelectSideWarning] =
    useState(false);

  const navigate = useNavigate();

  const escapeToClose = useCallback(
    e => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', escapeToClose);
    return () => {
      document.removeEventListener('keydown', escapeToClose);
    };
  }, [escapeToClose]);

  function handleClickSideToDrop({productViewId}) {
    if (!productViewId) return;

    switch (productViewId) {
      case VIEW_ID_MAP.front:
        metrics.pageViewTag('/ndx/quote/pricetip/dropfront');
        break;
      case VIEW_ID_MAP.back:
        metrics.pageViewTag('/ndx/quote/pricetip/dropback');
        break;
    }

    setShowMustSelectSideWarning(false);
    setSelectedProductViewId(productViewId);
  }

  function handleEditDesign() {
    metrics.pageViewTag('/ndx/quote/pricetip/editdesign');
    navigate('/');
    onClose();
  }

  function handleRemoveSelectedSide() {
    if (!selectedProductViewId) {
      setShowMustSelectSideWarning(true);
      return;
    }

    if (!icons || !itemizedPricesWithoutDroppedView) {
      return;
    }

    metrics.pageViewTag('/ndx/quote/pricetip/removeside');

    dispatch(
      removeIconsSelectedProductView({productViewId: selectedProductViewId})
    );
    dispatch(
      selectRemainingProductView({droppedViewId: selectedProductViewId})
    );
    dispatch(
      updateDroppedView({
        ...getDroppedViewInfo({
          productViewId: selectedProductViewId
        }),
        savings
      })
    );
    onClose();
  }

  function getFilteredProductViews() {
    return (
      productViews?.filter(view => {
        return DEFAULT_VIEWS.includes(view?.name);
      }) || []
    );
  }

  function render() {
    if (!icons || !selectedProduct || !selectedProductColor) {
      return <></>;
    }

    const productViewsCanvases = getFilteredProductViews()
      .map(productView => {
        if (!productView) return null;

        const constraint = viewConstraint(productView);
        const productViewId = productView.id;
        const isSelected =
          selectedProductViewId === null
            ? false
            : productViewId === selectedProductViewId;
        if (!constraint) return null;

        return (
          <SideToDrop
            key={`dssm-side-${productViewId}`}
            selectedProduct={selectedProduct}
            selectedProductColor={selectedProductColor}
            productView={productView}
            viewConstraint={constraint}
            hideIcons={false}
            icons={icons}
            isSelected={isSelected}
            minimumConstraintDim={120}
            onClickCallback={handleClickSideToDrop}
            itemizedPrices={itemizedPrices}
            itemizedPricesWithoutDroppedView={itemizedPricesWithoutDroppedView}
          />
        );
      })
      .filter(item => {
        return item !== null;
      });

    if (!productViewsCanvases) return <></>;

    return (
      <div className="ndx-ModalContainer">
        <div className="ndx-DropSideCard">
          <ContentCard
            scrolling={false}
            title={'Money Saving Design Changes'}
            onNavBack={null}
            onNavClose={() => onClose()}>
            <div
              className={`ndx-DropSideHeader ${
                showMustSelectSideWarning ? 'show-warning' : ''
              }`}>
              <span className="ndx-MainQuestion">
                Which design side do you want to remove?
              </span>
              <span className="ndx-MustSelectSideToDrop">
                Select a side to remove it.
              </span>
            </div>
            <div
              className="ndx-SidesToDropContainer"
              data-testid="sides-to-drop">
              {productViewsCanvases}
            </div>
            <div className="ndx-ActionButtons">
              <Button
                className="ndx-ActionButtons-btn"
                variant="secondary"
                size="large"
                onClick={() => handleEditDesign()}>
                Edit Design
              </Button>
              <Button
                className="ndx-ActionButtons-btn"
                size="large"
                onClick={() => handleRemoveSelectedSide()}>
                Remove Selected Side
              </Button>
            </div>
          </ContentCard>
        </div>
      </div>
    );
  }

  return render();
};

const propTypes = {
  anchorStyle: PropTypes.object,
  getDroppedViewInfo: PropTypes.func,
  getProductViews: PropTypes.func,
  getViewConstraint: PropTypes.func,
  icons: PropTypes.arrayOf(PropTypes.object),
  itemizedPrices: PropTypes.object,
  itemizedPricesWithoutDroppedView: PropTypes.object,
  removeIconsSelectedProductView: PropTypes.func,
  selectedProduct: PropTypes.object,
  selectedProductColor: PropTypes.object,
  selectRemainingProductView: PropTypes.func,
  updateDroppedView: PropTypes.func,
  onClose: PropTypes.func
};

DropSideCard.propTypes = propTypes;

export default DropSideCard;
